import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { trpc } from "./trpc";

const TOKEN_STORAGE_KEY = "baby_cafe_guide_token";
const DEMO_TOKEN = "demo-admin-token-2024";

interface User {
  id: number;
  name: string;
  email: string | null;
  role: "user" | "admin";
  isAdmin: boolean;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (token: string) => void;
  loginDemo: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Demo user for testing
const DEMO_USER: User = {
  id: 1,
  name: "デモ管理者",
  email: "demo@example.com",
  role: "admin",
  isAdmin: true,
};

/**
 * Get the stored auth token
 */
export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Set the auth token
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

/**
 * Remove the auth token
 */
export function removeAuthToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Auth provider component
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [user, setUser] = useState<User | null>(() => {
    // Check for demo token on mount
    if (typeof window !== "undefined") {
      const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (storedToken === DEMO_TOKEN) return DEMO_USER;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check if using demo token
  const isDemoToken = token === DEMO_TOKEN;

  // Fetch user info when token changes (skip if demo token)
  const { data, isLoading: isQueryLoading, refetch } = trpc.auth.me.useQuery(undefined, {
    enabled: !!token && !isDemoToken,
    retry: false,
    onError: () => {
      // Token is invalid, clear it
      removeAuthToken();
      setToken(null);
      setUser(null);
    },
  });

  useEffect(() => {
    // Skip if demo token is active
    if (isDemoToken) {
      setUser(DEMO_USER);
      setIsLoading(false);
      return;
    }

    if (data) {
      setUser(data);
    } else if (!token) {
      setUser(null);
    }
    setIsLoading(isQueryLoading);
  }, [data, token, isQueryLoading, isDemoToken]);

  const login = useCallback((newToken: string) => {
    setAuthToken(newToken);
    setToken(newToken);
    refetch();
  }, [refetch]);

  const loginDemo = useCallback(() => {
    setAuthToken(DEMO_TOKEN);
    setToken(DEMO_TOKEN);
    setUser(DEMO_USER);
  }, []);

  const logout = useCallback(() => {
    removeAuthToken();
    setToken(null);
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin ?? false,
    login,
    loginDemo,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Component to require authentication
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">ログインが必要です</h1>
          <p className="mt-2 text-stone-600">
            管理画面にアクセスするにはCLIトークンが必要です。
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Component to require admin role
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">ログインが必要です</h1>
          <p className="mt-2 text-stone-600">
            管理画面にアクセスするにはCLIトークンが必要です。
          </p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">アクセス権限がありません</h1>
          <p className="mt-2 text-stone-600">
            このページにアクセスするには管理者権限が必要です。
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
